import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Dialog from 'src/componentes/dialog';
import Drawer from 'src/componentes/drawer';
import Tab from 'src/componentes/tabs/tab';
import Tabs from 'src/componentes/tabs/tabs';
import useAuth from 'src/hooks/useAuth';
import { capitalizeFirstLetter } from 'src/utils/string';
import GruposList from 'src/paginas/grupo-email/view';
import TipoResponsabilidadeList from 'src/paginas/EscritorioProjetos/FluxoAprovacaoGenerico/TipoResponsabilidade';
import ColaboradorLookupList from 'src/paginas/colaborador/ColaboradorLookup';
import Button from 'src/componentes/button';
import distinctBy from 'src/utils/ArrayDistinctBy';

const getSelectedItems = (selectedItems, key, exibirGrupos) => {
  if (!selectedItems) return [];
  if (!exibirGrupos) return selectedItems;

  return selectedItems.filter((item) => item.identificadorLista === key);
};

const normalizeItems = (key, itens, exibirGrupos) => {
  const newList = itens ? distinctBy(itens, 'id') : [];

  if (!exibirGrupos) return newList.map((item) => ({ ...item }));

  const prefixo = {
    colaboradores: 'C',
    grupos: 'G',
    responsabilidades: 'R',
  }[key];

  return newList.map((item) => ({
    ...item,
    idLookup: `${prefixo}-${item.id}`,
    identificadorLista: key,
    descricao: item.descricao ?? item.nome,
  }));
};

const normalizeColaborador = (item) => ({
  ...item,
  id: item.id ?? item.Id,
  nome: item.nome ?? item.Nome,
  idIntegracao: item.idIntegracao ?? item.IdIntegracao,
  unidadeArea: item.unidadeArea ?? item.UnidadeArea,
  fotoArquivo: item.fotoArquivo ?? item.FotoArquivo,
});

const SearchComponent = ({
  selectedCallback,
  selectedIds,
  selectedItems,
  parametersSearchComponent = {},
  ...rest
}) => {
  const intl = useIntl();
  const dialogRef = useRef();
  const { terms: resources } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const abrirEmDrawer = parametersSearchComponent?.abrirEmDrawer === true;
  const drawerPosition = parametersSearchComponent?.drawerPosition || 'right';
  const drawerSize = parametersSearchComponent?.drawerSize || 800;
  const drawerZIndex = parametersSearchComponent?.drawerZIndex || 12000;
  const filterDrawerZIndex = Number(drawerZIndex) + 1;
  const drawerFocusTrapPriority = Number(rest.index || 0) + 2;
  const searchComponentTitle =
    parametersSearchComponent?.tituloSearchComponent ||
    capitalizeFirstLetter(
      resources.responsaveis || resources.responsavel || resources.colaboradores
    );
  const exibirGrupos = parametersSearchComponent?.exibirGrupos === true;
  const exibirResponsabilidades =
    parametersSearchComponent?.exibirResponsabilidades === true;
  const ignorePermissions =
    parametersSearchComponent?.ignorePermissions === true;
  const currentSelectedItems =
    selectedItems?.length > 0 ? selectedItems : selectedIds;

  const [data, setData] = useState({
    colaboradores: getSelectedItems(
      currentSelectedItems,
      'colaboradores',
      exibirGrupos
    ),
    grupos: exibirGrupos
      ? getSelectedItems(currentSelectedItems, 'grupos', exibirGrupos)
      : [],
    responsabilidades: exibirResponsabilidades
      ? getSelectedItems(currentSelectedItems, 'responsabilidades', exibirGrupos)
      : [],
  });

  const handleClose = () => {
    if (abrirEmDrawer) {
      setDrawerOpen(false);
      setTimeout(() => {
        rest.remove && rest.remove();
      }, 500);
      return;
    }

    if (dialogRef?.current) dialogRef?.current.close();
  };

  const handleSelect = (key, itens) => {
    setData((prev) => ({
      ...prev,
      [key]: normalizeItems(key, itens, exibirGrupos),
    }));
  };

  const handleSave = () => {
    if (selectedCallback) {
      selectedCallback(
        exibirGrupos
          ? [...data.colaboradores, ...data.grupos, ...data.responsabilidades]
          : data.colaboradores
      );
    }

    handleClose();
  };

  const actions = [
    <Button key="cancelar" onClick={handleClose} color="secondary">
      {intl.formatMessage({ id: 'label.cancelar' })}
    </Button>,
    <Button
      key="confirmar"
      className="ml-2"
      type="primary"
      onClick={handleSave}
      htmlType="submit"
    >
      {intl.formatMessage({ id: 'label.confirmar' })}
    </Button>,
  ];

  const colaboradoresList = data?.colaboradores;
  const gruposColaboradoresList = data?.grupos;

  const renderColaboradoresList = () => (
    <ColaboradorLookupList
      multiple={rest.multiple}
      select
      selectRealTime
      selectedCallback={(itens) => handleSelect('colaboradores', itens)}
      selectedItems={colaboradoresList}
      selectedIds={colaboradoresList}
      parameters={{ ...rest.parameters }}
      showContent={false}
      showConfirmButton={false}
      ignorePermissions={ignorePermissions}
      usarFiltroSalvo={false}
      openLastFilter={false}
      onRemoveSaveFilter={false}
      hideColumnsButton
      fillAvailableHeight
      customListUrl="/ColaboradorLookup/ObterListaColaboradoresLookup"
      filterDrawerZIndex={filterDrawerZIndex}
      transformListItem={normalizeColaborador}
    />
  );

  const content = exibirGrupos ? (
    <Tabs
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
      }}
      styleContent={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        paddingBottom: 0,
      }}
    >
      <Tab
        label={intl.formatMessage({
          id: capitalizeFirstLetter(resources.colaboradores),
        })}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {renderColaboradoresList()}
      </Tab>
      <Tab
        label={intl.formatMessage({ id: 'grupos' })}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <GruposList
          multiple
          select
          selectRealTime
          showNew={false}
          showExport={false}
          usarFiltroSalvo={false}
          onRemoveSaveFilter={false}
          hideColumnsButton
          selectedCallback={(itens) => handleSelect('grupos', itens)}
          selectedItems={gruposColaboradoresList}
          selectedIds={gruposColaboradoresList}
          fillAvailableHeight
          filterDrawerZIndex={filterDrawerZIndex}
        />
      </Tab>
      {exibirResponsabilidades && (
        <Tab
          label={intl.formatMessage({ id: 'responsabilidades' })}
          className="tw:data-[state=active]:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden"
        >
          <TipoResponsabilidadeList
            multiple
            select
            selectRealTime
            selectedCallback={(itens) => handleSelect('responsabilidades', itens)}
            selectedItems={data.responsabilidades}
            selectedIds={data.responsabilidades}
            parameters={{
              ehGestaoOcorrencia: parametersSearchComponent.ehGestaoOcorrencia,
            }}
            showMenu={false}
            usarFiltroSalvo={false}
            onRemoveSaveFilter={false}
            hideColumnsButton
            fillAvailableHeight
            filterDrawerZIndex={filterDrawerZIndex}
          />
        </Tab>
      )}
    </Tabs>
  ) : (
    renderColaboradoresList()
  );

  if (abrirEmDrawer) {
    return (
      <Drawer
        open={drawerOpen}
        close={handleClose}
        title={searchComponentTitle}
        position={drawerPosition}
        size={drawerSize}
        zIndex={drawerZIndex}
        focusTrap
        focusTrapPriority={drawerFocusTrapPriority}
        actions={actions}
        actionsStyle={{ justifyContent: 'flex-end' }}
        bodyStyle={{
          display: 'flex',
          flex: '1 1 0',
          flexDirection: 'column',
          height: 'auto',
          minHeight: 0,
          overflow: 'hidden',
          paddingBottom: 0,
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      ref={dialogRef}
      title={searchComponentTitle}
      width={800}
      maxHeight="80%"
      actions={actions}
      padContent
      onRequestClose={handleClose}
      {...rest}
    >
      {content}
    </Dialog>
  );
};

export default SearchComponent;
